import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, Sparkles, Instagram, Facebook } from 'lucide-react';

const ContactPage = () => {
  return (
    <div className="pt-24 pb-32 bg-cream min-h-screen relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-[0.02] pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-24"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold/10 border border-gold/20 mb-6 text-gold font-bold text-[10px] uppercase tracking-[0.3em]">
            <Mail size={14} />
            Concierge Access
          </div>
          <h1 className="text-6xl md:text-9xl font-serif font-bold text-dark tracking-tighter mb-8 italic">
            Begin Your <br /><span className="text-gold not-italic">Transformation</span>
          </h1>
          <p className="text-xl text-gray-500 max-w-2xl mx-auto font-light leading-relaxed">
            Personalized consultations. Elite execution. Reach out to secure your moment of luxury.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-16">
          {/* Contact Details */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-1 space-y-12"
          >
            <div className="space-y-8">
              <h3 className="text-3xl font-serif font-bold text-dark tracking-tight">The Studio</h3>
              <div className="space-y-6">
                <div className="flex items-start gap-5 group">
                  <div className="p-4 rounded-2xl bg-white shadow-premium text-gold group-hover:bg-gold group-hover:text-white transition-all duration-500">
                    <Phone size={24} />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase font-black text-gold/60 tracking-widest mb-1">Direct Line</p>
                    <p className="text-xl font-bold text-dark">(704) 641-7227</p>
                  </div>
                </div>

                <div className="flex items-start gap-5 group">
                  <div className="p-4 rounded-2xl bg-white shadow-premium text-gold group-hover:bg-gold group-hover:text-white transition-all duration-500">
                    <MapPin size={24} />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase font-black text-gold/60 tracking-widest mb-1">Location</p>
                    <p className="text-xl font-bold text-dark">Charlotte, North Carolina</p>
                  </div>
                </div>

                <div className="flex items-start gap-5 group">
                  <div className="p-4 rounded-2xl bg-white shadow-premium text-gold group-hover:bg-gold group-hover:text-white transition-all duration-500">
                    <Clock size={24} />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase font-black text-gold/60 tracking-widest mb-1">Availability</p>
                    <p className="text-xl font-bold text-dark">Mon-Sat: 9am - 7pm</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-8 pt-8 border-t border-gold/10">
              <h3 className="text-xl font-serif font-bold text-dark tracking-tight italic">Digital Connection</h3>
              <div className="flex gap-4">
                {[
                  { icon: <Instagram size={20} />, label: "Instagram" },
                  { icon: <Facebook size={20} />, label: "Facebook" }
                ].map((social, i) => (
                  <motion.a
                    key={i}
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    href="#"
                    className="p-4 rounded-full bg-white shadow-sm text-dark hover:text-gold transition-colors block border border-gold/5"
                  >
                    {social.icon}
                  </motion.a>
                ))}
                <motion.a
                  whileHover={{ scale: 1.1, rotate: -5 }}
                  href="https://vt.tiktok.com/ZSaP8PKkS/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-4 rounded-full bg-white shadow-sm text-dark hover:text-gold transition-colors block border border-gold/5"
                >
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.03 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.28-2.26.74-4.63 2.58-5.91 1.64-1.15 3.7-1.49 5.66-1.02v4.53c-.31-.19-.71-.24-1.07-.26-.67-.03-1.4.03-2.03.35-.8.41-1.41 1.18-1.48 2.08-.1 1.28.82 2.51 2.1 2.78.36.08.73.1 1.1.1 1.65-.05 3.14-1.31 3.4-2.94.02-.15.03-.3.03-.45V0c-.01 0 .01 0 0 .02z" />
                  </svg>
                </motion.a>
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          {/* Direct Line Priority */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="lg:col-span-2 bg-dark rounded-[50px] p-12 md:p-20 shadow-platinum relative overflow-hidden text-center flex flex-col justify-center items-center h-full border border-gold/20"
          >
            <div className="absolute top-0 right-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-[0.03]"></div>

            <div className="relative z-10 space-y-8">
              <div className="inline-block p-6 rounded-full bg-gradient-gold shadow-[0_0_30px_rgba(212,175,55,0.3)] mb-4 animate-pulse">
                <Phone size={40} className="text-dark" />
              </div>

              <div>
                <h2 className="text-4xl md:text-6xl font-serif font-bold text-white mb-2 tracking-tighter">Ready to Transform?</h2>
                <p className="text-gray-400 text-lg">We prioritize direct voice communication to ensure every detail is perfect.</p>
              </div>

              <div className="py-8">
                <a href="tel:17046417227" className="text-5xl md:text-7xl font-bold text-gold hover:text-white transition-colors duration-300 font-serif tracking-tight">
                  (704) 641-7227
                </a>
              </div>

              <div className="flex flex-col sm:flex-row justify-center gap-6">
                <a href="tel:17046417227" className="bg-white text-dark px-12 py-4 rounded-full font-bold uppercase tracking-widest hover:scale-105 transition-transform">
                  Call Now
                </a>
                <a href="sms:17046417227" className="border border-white/20 text-white px-12 py-4 rounded-full font-bold uppercase tracking-widest hover:bg-white/10 hover:border-white transition-all">
                  Text Us
                </a>
              </div>

              <div className="flex items-center justify-center gap-3 text-gold/40 mt-8">
                <Sparkles size={12} />
                <span className="text-[9px] uppercase tracking-[0.2em] font-bold">Fastest Response Time</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
